/* eslint-disable max-lines */
import { Download, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { useDtfArtworks } from "@design-lab/entities/artwork";
import { STANDARD_DTF_CATALOG_NUMBER } from "@design-lab/shared/constants/constants";
import { useDebounce } from "@design-lab/shared/hooks/useDebounce";
import { useDtfChannel } from "@design-lab/shared/hooks/useDtfChannel";
import { downloadFile } from "@design-lab/shared/lib/file";
import { Button, Checkbox } from "@design-lab/shared/ui";
import { Input } from "@design-lab/shared/ui/input";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@design-lab/shared/ui/modal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@design-lab/shared/ui/select";
import { pluralize } from "common/utils/format/word";

type MyDesignLibraryProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectArtworks?: (artworkIds: number[]) => void;
  allowMultipleSelection?: boolean;
  maxSelectionCount?: number;
};

export function MyDesignLibrary(props: MyDesignLibraryProps) {
  const {
    isOpen,
    onClose,
    onSelectArtworks,
    allowMultipleSelection = false,
    maxSelectionCount = 10
  } = props;
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [sort, setSort] = useState("desc");
  const [dtfChannelUuid, setDtfChannelUuid] = useState<string>("");
  const [selectedArtworks, setSelectedArtworks] = useState<Set<number>>(new Set());

  const channel = useDtfChannel();

  useEffect(() => {
    const fetchChannelUuid = async () => {
      const uuid = await channel.retrieveDtfChannelUuid();
      setDtfChannelUuid(uuid || "");
    };

    fetchChannelUuid();
  }, [channel]);

  // Reset selections when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedArtworks(new Set());
    }
  }, [isOpen]);

  const { data: dtfArtworks, isLoading: isLoadingDtfArtworks } = useDtfArtworks({
    search: debouncedSearch,
    sortOrder: sort,
    artworksType: "uploaded",
    dtfChannelUuid,
    catalogNumber: STANDARD_DTF_CATALOG_NUMBER
  });

  const handleSelectDesign = () => {
    if (selectedArtworks.size === 0) return;

    const artworkIds = Array.from(selectedArtworks);

    if (onSelectArtworks) {
      onSelectArtworks(artworkIds);
    }
    onClose();
  };

  const handleToggleArtwork = (artworkId: number) => {
    setSelectedArtworks(prev => {
      const newSelected = new Set(prev);

      if (allowMultipleSelection) {
        // Multiple selection mode
        if (newSelected.has(artworkId)) {
          newSelected.delete(artworkId);
        } else if (newSelected.size < maxSelectionCount) {
          // Check if we've reached the max selection limit
          newSelected.add(artworkId);
        }
      } else if (newSelected.has(artworkId)) {
        // Single selection mode - deselect if already selected
        newSelected.delete(artworkId);
      } else {
        // Single selection mode - clear previous and select new one
        newSelected.clear();
        newSelected.add(artworkId);
      }

      return newSelected;
    });
  };

  const getResultsContent = () => {
    // Show loader if waiting for catalog number or loading artworks
    if (isLoadingDtfArtworks) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4">
          <Loader2 className="size-8 animate-spin" />
          <p className="text-sm text-gray-600">Loading designs...</p>
        </div>
      );
    }

    if (dtfArtworks?.artworks.length === 0) {
      return (
        <div className="flex h-full items-center justify-center">
          <p>No designs found</p>
        </div>
      );
    }

    return (
      <div className="grid flex-1 grid-cols-2 gap-8 overflow-y-auto md:grid-cols-3 lg:grid-cols-4">
        {dtfArtworks?.artworks.map(artwork => (
          <div key={artwork.id} className="relative w-full text-sm">
            <div className="absolute top-2 flex w-full items-center justify-between px-2">
              <Checkbox
                data-testid={`artwork-${artwork.id}-checkbox`}
                isSelected={selectedArtworks.has(artwork.id)}
                onChange={() => handleToggleArtwork(artwork.id)}
                isDisabled={
                  allowMultipleSelection &&
                  !selectedArtworks.has(artwork.id) &&
                  selectedArtworks.size >= maxSelectionCount
                }
              />

              <Button
                data-testid={`download-artwork-${artwork.id}-button`}
                variant="outline"
                size="icon"
                onPress={() => downloadFile(artwork.original.imageUrl, artwork.fileName)}
              >
                <Download className="size-4" />
              </Button>
            </div>
            <div
              key={artwork.id}
              className="flex h-48 cursor-pointer items-center overflow-hidden rounded-md border border-gray-300 transition-colors hover:border-gray-400"
              onClick={() => handleToggleArtwork(artwork.id)}
            >
              <img src={artwork.original.imageUrl} alt={artwork.fileName} />
            </div>
            <p className="truncate">{artwork.fileName}</p>
            <p>
              Size: {artwork.original.width} x {artwork.original.height}
            </p>
            <p>Uploaded on: {artwork.uploadedOn}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => onClose()}
      closeOnBackdropClick={false}
      closeOnEsc={false}
      className="flex h-[80%] w-[96%] flex-col lg:h-[60vh] lg:w-[60rem]"
    >
      <ModalHeader onClose={() => onClose()}>My Library</ModalHeader>
      <ModalBody className="flex flex-1 flex-col space-y-4 overflow-hidden">
        <div className="flex items-center gap-4">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select-trigger" className="lg:w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Newest First</SelectItem>
              <SelectItem value="asc">Oldest First</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative lg:w-[200px]">
            <Input
              data-testid="search-input"
              placeholder="Search image names"
              className="w-full pr-8"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button
                data-testid="mydesign-clear-search"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
        {getResultsContent()}
      </ModalBody>
      {!isLoadingDtfArtworks && selectedArtworks.size > 0 && (
        <ModalFooter className="flex justify-between">
          <div className="flex items-center text-sm text-gray-600">
            {allowMultipleSelection && (
              <span>
                {selectedArtworks.size} of {maxSelectionCount} selected
              </span>
            )}
          </div>
          <div className="flex gap-4">
            <Button data-testid="cancel-button" variant="outline" onPress={() => onClose()}>
              Cancel
            </Button>
            <Button data-testid="select-design-button" onPress={handleSelectDesign}>
              {allowMultipleSelection
                ? `Select ${pluralize(selectedArtworks.size, "Design", "s", true)}`
                : "Select Design"}
            </Button>
          </div>
        </ModalFooter>
      )}
    </Modal>
  );
}
